import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import MUISelect from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import Popper from '@mui/material/Popper';
import { styled as muiStyled } from '@mui/material/styles';
import { Search as SearchIcon, Filter as FilterIcon, X as XIcon } from 'react-feather';
import type { GeneratedMessage } from '@trinity/chat/types/message-generation';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { Constants } from '@trinity/chat-components/components/Theme';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion.js';

type Props = {
  segmentId?: string;
  segmentationSessionId?: string;
  segmentName?: string;
  onMessageClick?: (message: GeneratedMessage) => void;
  onAddMessage?: () => void;
  onBulkUpload?: () => void;
  selectionMode?: boolean;
  onToggleSelectionMode?: () => void;
  selectedMessageIds?: Set<number>;
  onToggleSelection?: (messageId: number) => void;
  onPopulateSurvey?: (selectedMessages: GeneratedMessage[]) => void;
};

export const MessageLibrarySidePanel = ({ segmentId, segmentationSessionId, segmentName, onMessageClick, onAddMessage, onBulkUpload, selectionMode, onToggleSelectionMode, selectedMessageIds, onToggleSelection, onPopulateSurvey }: Props) => {
  const apiClient = useChatApiClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<{ category: string | null; status: string | null; confidence: string | null }>({
    category: null,
    status: null,
    confidence: null,
  });
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const filterPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFilterOpen) return;

    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (filterButtonRef.current?.contains(target)) return;
      if (filterPopoverRef.current?.contains(target)) return;
      const muiPopover = (target as Element).closest?.('.MuiPopover-root, .MuiModal-root, .MuiMenu-root');
      if (muiPopover) return;
      setIsFilterOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterOpen]);

  // Fetch messages for the segment
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['generated-messages', { segmentId, segmentationSessionId }],
    queryFn: async () => {
      if (!segmentId) return [];
      const params: Record<string, string> = { segmentId };
      if (segmentationSessionId) {
        params.segmentationSessionId = segmentationSessionId;
      }
      return apiClient.messageGeneration.getAllMessages(params);
    },
    enabled: !!segmentId,
  });

  const filterOptions = useMemo(() => {
    const categories = new Set<string>();
    const statuses = new Set<string>();
    const confidences = new Set<string>();
    for (const m of messages) {
      if (m.category) categories.add(m.category);
      if (m.status) statuses.add(m.status);
      if (m.confidence) confidences.add(m.confidence);
    }
    return {
      categories: Array.from(categories).sort(),
      statuses: Array.from(statuses).sort(),
      confidences: Array.from(confidences).sort(),
    };
  }, [messages]);

  const activeFilterCount = [filters.category, filters.status, filters.confidence].filter(Boolean).length;

  const filteredMessages = useMemo(() => {
    let result = messages;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.body.toLowerCase().includes(q) ||
          (m.category && m.category.toLowerCase().includes(q))
      );
    }
    if (filters.category) {
      result = result.filter((m) => m.category === filters.category);
    }
    if (filters.status) {
      result = result.filter((m) => m.status === filters.status);
    }
    if (filters.confidence) {
      result = result.filter((m) => m.confidence === filters.confidence);
    }
    return result;
  }, [messages, searchQuery, filters]);

  const handleMessageItemClick = useCallback(
    (message: GeneratedMessage) => {
      onMessageClick?.(message);
    },
    [onMessageClick]
  );

  // Generate message identifier (E1, C1, etc.) - matches MessageGenerationTable logic
  const getMessageId = (message: GeneratedMessage, index: number) => {
    // Try objectiveCategory first, then message category, then fallback to 'M'
    const source = message.objectiveCategory || message.category;
    if (!source) return `M${index + 1}`;
    const firstLetter = source.trim().charAt(0).toUpperCase();

    // Count how many messages of the same category come before this one
    const categoryCount = messages
      .slice(0, index + 1)
      .filter((m) => {
        const mSource = message.objectiveCategory || m.category;
        return mSource && mSource.trim().charAt(0).toUpperCase() === firstLetter;
      }).length;

    return `${firstLetter}${categoryCount}`;
  };

  const handleClearFilters = (): void => {
    setFilters({ category: null, status: null, confidence: null });
  };

  const selectMenuProps = useMemo(() => ({
    PaperProps: {
      sx: {
        borderRadius: '4px',
        backgroundColor: 'var(--pri-01, #fff)',
        boxShadow: 'var(--shadow, 0 2px 6px 2px rgba(60, 64, 67, .15))',
      },
    },
    MenuListProps: {
      sx: { padding: 0 },
    },
    sx: { zIndex: '1400 !important' } as const,
    disableScrollLock: true,
    transitionDuration: 80,
  }), []);

  return (
    <Root>
      <Content>
          <Header>
            <StyledAutoAwesomeIcon />
            <Title>Segment Messages</Title>
            <MessageCount>{filteredMessages.length}</MessageCount>
            {onBulkUpload && (
              <BulkUploadIconButton onClick={onBulkUpload} title="Bulk upload messages from CSV">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </BulkUploadIconButton>
            )}
            {onAddMessage && (
              <AddMessageIconButton onClick={onAddMessage} title="Add message for this segment">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </AddMessageIconButton>
            )}
          </Header>
          <ToolbarRow>
            <ToolbarLeft>
              <ToolbarIconButton
                onClick={() => setIsSearchOpen((v) => !v)}
                $isActive={isSearchOpen || !!searchQuery}
                title="Search messages"
                aria-label="Search messages"
              >
                <SearchIcon width={16} height={16} />
              </ToolbarIconButton>
              <span>
                  <ToolbarIconButton
                    ref={filterButtonRef}
                    onClick={() => setIsFilterOpen((v) => !v)}
                    $isActive={activeFilterCount > 0}
                    title="Filter messages"
                    aria-label="Filter messages"
                  >
                    <FilterIconContainer>
                      <FilterIcon width={16} height={16} />
                      {activeFilterCount > 0 && <ActiveDot />}
                    </FilterIconContainer>
                    {activeFilterCount > 0 && (
                      <>
                        <ToolbarDivider />
                        <ToolbarBadgeText>({activeFilterCount})</ToolbarBadgeText>
                      </>
                    )}
                  </ToolbarIconButton>
                  <Popper
                    open={isFilterOpen}
                    anchorEl={filterButtonRef.current}
                    placement="bottom-start"
                    style={{ zIndex: 1300 }}
                  >
                    <FilterPopover ref={filterPopoverRef}>
                      <FilterPopoverHeader>
                        <FilterPopoverTitle>Filters</FilterPopoverTitle>
                        {activeFilterCount > 0 && (
                          <ClearFiltersButton onClick={handleClearFilters}>
                            Clear all
                          </ClearFiltersButton>
                        )}
                      </FilterPopoverHeader>
                      <FilterRow>
                        <FilterLabel>Category</FilterLabel>
                        <MUISelect
                          value={filters.category ?? ''}
                          onChange={(e: SelectChangeEvent<string>) => setFilters((f) => ({ ...f, category: e.target.value || null }))}
                          displayEmpty
                          input={<SelectInput />}
                          MenuProps={selectMenuProps}
                          renderValue={(v) => (v ? String(v) : 'All')}
                          size="small"
                        >
                          <StyledMenuItem value="">All</StyledMenuItem>
                          {filterOptions.categories.map((c) => (
                            <StyledMenuItem key={c} value={c}>{c}</StyledMenuItem>
                          ))}
                        </MUISelect>
                      </FilterRow>
                      <FilterRow>
                        <FilterLabel>Status</FilterLabel>
                        <MUISelect
                          value={filters.status ?? ''}
                          onChange={(e: SelectChangeEvent<string>) => setFilters((f) => ({ ...f, status: e.target.value || null }))}
                          displayEmpty
                          input={<SelectInput />}
                          MenuProps={selectMenuProps}
                          renderValue={(v) => {
                            if (!v) return 'All';
                            return String(v).charAt(0).toUpperCase() + String(v).slice(1);
                          }}
                          size="small"
                        >
                          <StyledMenuItem value="">All</StyledMenuItem>
                          {filterOptions.statuses.map((s) => (
                            <StyledMenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</StyledMenuItem>
                          ))}
                        </MUISelect>
                      </FilterRow>
                      <FilterRow>
                        <FilterLabel>Confidence</FilterLabel>
                        <MUISelect
                          value={filters.confidence ?? ''}
                          onChange={(e: SelectChangeEvent<string>) => setFilters((f) => ({ ...f, confidence: e.target.value || null }))}
                          displayEmpty
                          input={<SelectInput />}
                          MenuProps={selectMenuProps}
                          renderValue={(v) => {
                            if (!v) return 'All';
                            return String(v).charAt(0).toUpperCase() + String(v).slice(1);
                          }}
                          size="small"
                        >
                          <StyledMenuItem value="">All</StyledMenuItem>
                          {filterOptions.confidences.map((c) => (
                            <StyledMenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</StyledMenuItem>
                          ))}
                        </MUISelect>
                      </FilterRow>
                    </FilterPopover>
                  </Popper>
                </span>
            </ToolbarLeft>
            {onToggleSelectionMode && (
              <SelectTextButton
                onClick={onToggleSelectionMode}
                $isActive={!!selectionMode}
              >
                {selectionMode ? 'Cancel' : 'Select'}
              </SelectTextButton>
            )}
          </ToolbarRow>
          {isSearchOpen && (
            <SearchBar>
              <SearchInputIcon>
                <SearchIcon width={14} height={14} />
              </SearchInputIcon>
              <SearchInput
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <SearchClearButton onClick={() => setSearchQuery('')} aria-label="Clear search">
                  <XIcon width={14} height={14} />
                </SearchClearButton>
              )}
            </SearchBar>
          )}
          <MessagesScrollArea>
            {isLoading && (
              <EmptyState>
                Loading messages...
                <CircularProgress size={20} thickness={4} sx={{ color: '#999', marginTop: '8px' }} />
              </EmptyState>
            )}
            {!isLoading && messages.length === 0 && (
              <EmptyState>No messages generated for this segment</EmptyState>
            )}
            {!isLoading && filteredMessages.length === 0 && messages.length > 0 && (
              <EmptyState>No messages match your search or filters</EmptyState>
            )}
            {!isLoading && filteredMessages.map((message, index) => {
              const tooltipContent = (
                <TooltipContent>
                  <TooltipTitle>{message.title}</TooltipTitle>
                  <TooltipBody>{message.body}</TooltipBody>
                  <TooltipFooter>
                    Click to ask this message to {segmentName || 'segment'}
                  </TooltipFooter>
                </TooltipContent>
              );

              return (
                <Tooltip
                  key={message.id}
                  title={tooltipContent}
                  placement="left"
                  arrow
                  enterDelay={300}
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  }}
                >
                  <MessageItem
                    onClick={() => {
                      if (selectionMode && onToggleSelection) {
                        onToggleSelection(message.id);
                      } else {
                        handleMessageItemClick(message);
                      }
                    }}
                    $selected={!!selectionMode && !!selectedMessageIds?.has(message.id)}
                  >
                    <MessageHeader>
                      <MessageId>{getMessageId(message, index)}</MessageId>
                      <MessageHeaderRight>
                        {message.category && (
                          <CategoryBadge>{message.category}</CategoryBadge>
                        )}
                        {selectionMode && (
                          <SelectionCheckbox
                            $checked={!!selectedMessageIds?.has(message.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleSelection?.(message.id);
                            }}
                          >
                            {selectedMessageIds?.has(message.id) && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </SelectionCheckbox>
                        )}
                      </MessageHeaderRight>
                    </MessageHeader>
                    <MessageTitle>{message.title}</MessageTitle>
                    <MessageBody>{message.body}</MessageBody>
                    {message.prioritizationObjective && (
                      <MetadataRow>
                        <MetadataLabel>Objective:</MetadataLabel>
                        <MetadataValue>{message.prioritizationObjective}</MetadataValue>
                      </MetadataRow>
                    )}
                    {message.curationSegments && message.curationSegments.length > 0 && (
                      <MetadataRow>
                        <MetadataLabel>Segments:</MetadataLabel>
                        <CurationSegmentBadges>
                          {message.curationSegments.map((s) => (
                            <CurationSegmentBadge key={s.identifier}>{s.name}</CurationSegmentBadge>
                          ))}
                        </CurationSegmentBadges>
                      </MetadataRow>
                    )}
                    {message.confidence && (
                      <ConfidenceRow>
                        <ConfidenceLabel>Confidence:</ConfidenceLabel>
                        <ConfidenceValue>{message.confidence}</ConfidenceValue>
                      </ConfidenceRow>
                    )}
                  </MessageItem>
                </Tooltip>
              );
            })}
            {selectionMode && (
              <FloatingBar>
                <PopulateButton
                  disabled={!selectedMessageIds || selectedMessageIds.size === 0}
                  onClick={() => {
                    if (!selectedMessageIds || selectedMessageIds.size === 0 || !onPopulateSurvey) return;
                    const selected = messages.filter((m) => selectedMessageIds.has(m.id));
                    onPopulateSurvey(selected);
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: '#fff' }} />
                  Populate Survey Template ({selectedMessageIds?.size ?? 0})
                </PopulateButton>
              </FloatingBar>
            )}
          </MessagesScrollArea>
        </Content>
    </Root>
  );
};

MessageLibrarySidePanel.displayName = 'MessageLibrary.SidePanel';

const Root = styled.div`
  box-sizing: border-box;
  border-radius: ${Constants.Modal.BorderRadius}px;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.palette.gray.light2};
  position: relative;
`;

const Content = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--pri-01);
`;

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px 0;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 4px;
  gap: 4px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ToolbarIconButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive'),
})<{ $isActive?: boolean }>(({ $isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  background: $isActive ? 'var(--blue-l, #e8f4fd)' : 'transparent',
  padding: '6px 8px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 500,
  fontSize: '14px',
  transition: 'all 0.2s ease',
  outline: 'none',
  borderRadius: '6px',
  border: 'none',
  color: $isActive ? 'var(--blue-d, #1976d2)' : 'var(--gray-d, #555)',
  whiteSpace: 'nowrap' as const,
  '&:hover': {
    background: $isActive ? 'var(--blue-l, #e8f4fd)' : 'var(--gray-xl, #f5f5f5)',
  },
}));

const SelectTextButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive'),
})<{ $isActive?: boolean }>(() => ({
  background: 'none',
  border: 'none',
  padding: '4px 8px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  color: '#6366f1',
  borderRadius: '4px',
  transition: 'all 0.15s',
  '&:hover': {
    background: '#f5f3ff',
  },
}));

const FilterIconContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
});

const ActiveDot = styled.span({
  position: 'absolute',
  height: '6px',
  width: '6px',
  borderRadius: '50%',
  backgroundColor: 'var(--pri-02, #28AAE1)',
  right: '-3px',
  top: '-2px',
  border: '1.5px solid white',
});

const ToolbarDivider = styled.div({
  width: '1px',
  height: '14px',
  backgroundColor: 'var(--gray-m, #ccc)',
  marginLeft: '6px',
  marginRight: '4px',
});

const ToolbarBadgeText = styled.span({
  fontSize: '12px',
  fontWeight: 'bold',
});

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  margin: 0 8px 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  padding: 0 8px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: var(--pri-02, #28AAE1);
  }
`;

const SearchInputIcon = styled.span`
  display: flex;
  align-items: center;
  color: #999;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  padding: 7px 6px;
  font-size: 13px;
  font-family: inherit;
  background: transparent;
  color: #333;

  &::placeholder {
    color: #aaa;
  }
`;

const SearchClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 2px;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    color: #666;
    background: #f0f0f0;
  }
`;

const FilterPopover = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 12px;
  min-width: 200px;
  margin-top: 4px;
`;

const FilterPopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const FilterPopoverTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #333;
`;

const ClearFiltersButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: var(--pri-02, #28AAE1);
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const SelectInput = muiStyled(InputBase)(({ theme }) => ({
  '& .MuiSelect-select': {
    backgroundColor: 'var(--pri-01, #fff)',
    border: '1px solid var(--gray-l, #e0e0e0)',
    borderRadius: 4,
    color: 'var(--black-l, #333)',
    fontFamily: 'var(--font-reg, inherit)',
    fontSize: 13,
    padding: '5px 26px 5px 12px',
    position: 'relative' as const,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      backgroundColor: 'var(--pri-01, #fff)',
      borderColor: 'var(--gray-l, #e0e0e0)',
      borderRadius: 4,
      boxShadow: 'none',
    },
  },
}));

const StyledMenuItem = styled(MenuItem)`
  font-family: var(--font-reg, inherit);
  color: var(--gray-d, #555);
  font-size: 13px;
  background-color: var(--pri-01, #fff);
  min-height: 36px;
  margin: 0;

  &:hover,
  &.Mui-selected:hover,
  &.Mui-selected {
    background-color: var(--blue-l, #e8f4fd);
  }

  & .MuiTouchRipple-root {
    visibility: hidden;
  }
`;

const MessageCount = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  background: #e5e5e5;
  padding: 4px 8px;
  border-radius: 12px;
  line-height: 1;
`;

const BulkUploadIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #6366f1;
  border-radius: 6px;
  background: white;
  color: #6366f1;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    background: #eef2ff;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const AddMessageIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: #6366f1;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #4f46e5;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const MessagesScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const MessageItem = styled('div', {
  shouldForwardProp: shouldForwardProp('$selected'),
})<{ $selected?: boolean }>`
  background: ${(p) => (p.$selected ? '#f0f7ff' : '#ffffff')};
  border: 1px solid ${(p) => (p.$selected ? '#1271c4' : '#e5e5e5')};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.palette.sentiment.primary.main};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SelectionCheckbox = styled('div', {
  shouldForwardProp: shouldForwardProp('$checked'),
})<{ $checked?: boolean }>`
  width: 18px;
  height: 18px;
  border: 2px solid ${(p) => (p.$checked ? '#6366f1' : '#ccc')};
  border-radius: 4px;
  background: ${(p) => (p.$checked ? '#6366f1' : '#fff')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.15s;
  color: #fff;

  &:hover {
    border-color: #6366f1;
  }
`;

const FloatingBar = styled.div`
  position: sticky;
  bottom: 48px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  z-index: 5;
`;

const PopulateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: #6366f1;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);

  &:hover:not(:disabled) {
    background: #4f46e5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const MessageHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MessageId = styled.div`
  font-size: 11px;
  font-family: var(--font-bold);
  color: ${({ theme }) => theme.palette.sentiment.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CategoryBadge = styled.div`
  font-size: 10px;
  color: #666;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: capitalize;
`;

const MessageTitle = styled.div`
  font-size: 13px;
  font-family: var(--font-bold);
  color: #333;
  margin-bottom: 4px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const MessageBody = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const MetadataRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f0f0;
`;

const MetadataLabel = styled.span`
  font-size: 10px;
  color: #999;
  flex-shrink: 0;
`;

const MetadataValue = styled.span`
  font-size: 10px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const CurationSegmentBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
`;

const CurationSegmentBadge = styled.span`
  font-size: 9px;
  color: #666;
  padding: 1px 5px;
  white-space: nowrap;
`;

const ConfidenceRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f0f0;
`;

const ConfidenceLabel = styled.span`
  font-size: 10px;
  color: #999;
  margin-right: 4px;
`;

const ConfidenceValue = styled.span`
  font-size: 10px;
  font-family: var(--font-bold);
  color: #666;
  text-transform: capitalize;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 350px;
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.4;
`;

const TooltipBody = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  white-space: pre-wrap;
`;

const TooltipFooter = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 4px;
`;
